import { Component, OnInit, OnDestroy } from '@angular/core'; // Added OnDestroy
import { CommonModule } from '@angular/common';
import { FileUploadModule, FileUploadEvent, FileSelectEvent, FileRemoveEvent } from 'primeng/fileupload';
import { MessageModule } from 'primeng/message';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TabViewModule } from 'primeng/tabview';
import { ProgressBarModule } from 'primeng/progressbar';
import { ButtonModule } from 'primeng/button';
import { takeUntil, Subject, interval, Subscription } from 'rxjs';

import { TranslateService } from '../../../services/translate.service';
import { DirectionService } from '../../../services/direction.service';
import { AjaxService } from '../../../services/ajax.service';
import { ViewScopeService } from '../../../services/view-scope.service';
import { Pagination } from '../../../services/pagination.abstruct';

import { FILE_UPD, GET_ALL_JOBS } from '../../../services/account-api.constants';

// Existing interface for local file uploads
interface UploadedFileInfo {
	id: string;
	file: File;
	status: 'Pending' | 'Uploading' | 'Uploaded' | 'Failed';
	disp_status: string;
}

// NEW: Interface for the API job data
interface JobData {
	FileRecID: string;
	JobRecID: string;
	OriginalFileName: string;
	JobRunningStatus: string; // This will contain the full status description
}

@Component({
	selector: 'app-upload-file',
	standalone: true,
	imports: [
		CommonModule,
		FileUploadModule,
		MessageModule,
		ToastModule,
		TableModule,
		TagModule,
		TabViewModule,
		ProgressBarModule,
		ButtonModule
	],
	templateUrl: './upload-file.component.html',
	styleUrls: ['./upload-file.component.scss'],
	providers: [MessageService]
})
export class UploadFileComponent extends Pagination implements OnInit, OnDestroy {
	activeTabIndex: number = 0;

	uploadedFileInfo: UploadedFileInfo[] = [];

	// API endpoint for file uploads
	uploadApiUrl: string;

	private allowedFileTypesRegex: RegExp;
	private jobStatusApiUrl: string;

	// NEW: Properties for the Job Status table
	jobStatusData: JobData[] = [];
	jobStatusLoading: boolean = true;
	jobStatusRefreshSubscription!: Subscription;
	private destroy$ = new Subject<void>();

	// NEW: Define the desired order of statuses for progress calculation
	orderedStatuses: string[] = [
		'Uploaded / Ready to execution', // 0%
		'Preprocessing',
		'Ready to GCP processing',
		'GCP processing',
		'Ready to postprocess',
		'Postprocessing',
		'Ready to pre-journal step',
		'Completed', // 100%
		'Unknown Status' // Treat as 0% or N/A
	];


	constructor(
		private messageService: MessageService,
		public translateServ: TranslateService,
		public directionServ: DirectionService,
		private ajaxService: AjaxService,
		public viewScopeService: ViewScopeService) 
		{
			super();
		this.uploadApiUrl = this.ajaxService.getBaseOfApi() + FILE_UPD + '?userId=' + this.viewScopeService.getCurrentOpertaingUserId().toString() + "&customerRecID=" + this.getCurrentCustomer();
		const acceptAttribute = "application/pdf,image/*,image/tiff,image/tif";
		this.allowedFileTypesRegex = this.buildAcceptRegex(acceptAttribute);
		this.jobStatusApiUrl = this.ajaxService.getBaseOfApi() + GET_ALL_JOBS + this.getCurrentCustomer() + "/" + this.viewScopeService.getCurrentOpertaingUserId().toString();
	}





	getCurrentCustomer() {
		const selectedCustomerString = localStorage.getItem('selectedCustomer');

		if (selectedCustomerString) {
			return JSON.parse(selectedCustomerString).id;
		}
		else {
			console.log('No customer data found in localStorage.');
		}
	}

	async ngOnInit(): Promise<void> {
		// Check for unexported files/journals for current customer
		this.viewScopeService.getPendingWorkForCustomer(true).subscribe((pending: any[]) => {
			if (Array.isArray(pending) && pending.length > 0) {
				// Check if any NumberOfFiles or NumberOfJournalRows > 0
				const hasUnexported = pending.some(item => (item.NumberOfFiles && item.NumberOfFiles > 0) || (item.NumberOfJournalRows && item.NumberOfJournalRows > 0));
				if (hasUnexported) {
					this.messageService.add({
						severity: 'warn',
						summary: this.translateServ.dictionary['ATTENTION'],
						detail: this.translateServ.dictionary['ALREADY_FILES_OR_JOURNALS_NOT_EXPORTED'] || 'There are already some files and journals that were not exported!',
						life: 7000,
						sticky: false, // If I want the user to click manually to close the message.
						styleClass: 'blinking-warning'
					});
				}
			}
		});
		// Fetch job status data and set up auto-refresh
		this.fetchJobStatusData();
		this.setupJobStatusAutoRefresh();
	}

	ngOnDestroy(): void {
		// NEW: Unsubscribe from job status refresh subscription
		if (this.jobStatusRefreshSubscription) {
			this.jobStatusRefreshSubscription.unsubscribe();
		}
	}

	onFilesSelected(event: FileSelectEvent) {
		const filesArray: File[] = Array.from(event.files);
		let validFilesCount = 0;

		filesArray.forEach(file => {
			if (this.isValidFileType(file)) {
				const uniqueId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
				this.uploadedFileInfo.push({
					id: uniqueId,
					file: file,
					status: 'Pending',
					disp_status: this.translateServ.dictionary['STATUS_PENDING']
				});
				validFilesCount++;
			} else {
				this.messageService.add({
					severity: 'error',
					summary: 'Invalid File Type',
					detail: `'${file.name}' has an unsupported file type. Only PDF, images, and TIFF files are allowed.`,
					life: 7000,
					sticky: true,
					styleClass: 'blinking-warning'
				});
				console.warn(`File '${file.name}' was not added to the table due to invalid type.`);
			}
		});

		if (validFilesCount > 0) {
			this.messageService.add({ severity: 'info', summary: this.translateServ.dictionary['MSG_TITLE_FILES_SELECTED'], detail: `${validFilesCount} ${this.translateServ.dictionary['MSG_BDY_FILES_SELECTED']}`, life: 4000, sticky: false});
		} else {
			this.messageService.add({ severity: 'warn', summary: this.translateServ.dictionary['MSG_TITLE_NO_VALID_FILES'], detail: this.translateServ.dictionary['MSG_BDY_NO_VALID_FILES'], life: 4000, sticky: false});
		}
	}

	onUpload(event: FileUploadEvent) {
		const uploadedFilesArray: File[] = Array.from(event.files);
		uploadedFilesArray.forEach(uploadedFile => {
			const index = this.uploadedFileInfo.findIndex(item =>
				item.file.name === uploadedFile.name &&
				item.file.size === uploadedFile.size
			);
			if (index !== -1) {
				this.uploadedFileInfo[index].status = "Uploaded";
				this.uploadedFileInfo[index].disp_status = this.translateServ.dictionary['UPLOADED'];
				this.messageService.add({ severity: 'success', summary: this.translateServ.dictionary['UPLOADED'], detail: `${uploadedFile.name} ${this.translateServ.dictionary['PROCESSED']}.`, life: 7000});
			}
		});
		// Auto-advance to the next tab if all files are uploaded
		if (this.uploadedFileInfo.length > 0 && this.uploadedFileInfo.every(f => f.status === 'Uploaded')) {
			this.activeTabIndex = 1;
		}
		console.log('Files successfully uploaded to backend:', event.files);
	}

	onError(event: any) {
		const errorFilesArray: File[] = Array.from(event.files);
		errorFilesArray.forEach((file: File) => {
			const index = this.uploadedFileInfo.findIndex(item => item.file.name === file.name && item.file.size === file.size);
			if (index !== -1) {
				this.uploadedFileInfo[index].status = 'Failed';
				this.uploadedFileInfo[index].disp_status = this.translateServ.dictionary['FAILED'];
				this.messageService.add({ severity: 'error', summary: this.translateServ.dictionary['UPLOAD_FAILED'], detail: ` ${this.translateServ.dictionary['FAILED_TO_UPLOAD']} ${file.name}.`, life: 7000, sticky: false});
			}
		});
		console.error('Upload error:', event);
	}

	onFileRemoved(event: FileRemoveEvent) {
		const removedFile = event.file;
		this.uploadedFileInfo = this.uploadedFileInfo.filter(item =>
			!(item.file.name === removedFile.name &&
				item.file.size === removedFile.size)
		);
		this.messageService.add({ severity: 'warn', summary: this.translateServ.dictionary['FILE_REMOVED'], detail: `${removedFile.name} ${this.translateServ.dictionary['FILE_REMOVED_MSG']}.`, life: 4000, sticky: false});
		console.log('File removed:', removedFile.name);
		console.log('Updated uploadedFileInfo:', this.uploadedFileInfo);
	}

	getFileTypeLabel(fileType: string): string {
		if (!fileType) return this.translateServ.dictionary['DOC_TYPE_UNKOWN'];
		if (fileType === 'application/pdf') return this.translateServ.dictionary['DOC_TYPE_PDF'];
		if (fileType.startsWith('image/')) return this.translateServ.dictionary['DOC_TYPE_IMG'];
		if (fileType === 'image/tiff' || fileType === 'image/tif') return this.translateServ.dictionary['DOC_TYPE_TIFF'];
		return this.translateServ.dictionary['DOC_TYPE_UNKOWN'];
	}

	getFileTypeSeverity(fileType: string): string {
		if (!fileType) return 'secondary';
		if (fileType === 'application/pdf') return 'info';
		if (fileType.startsWith('image/')) return 'success';
		if (fileType === 'image/tiff' || fileType === 'image/tif') return 'warning';
		return 'secondary';
	}

	onClear() {
		this.uploadedFileInfo = [];
		this.messageService.add({ severity: 'info', summary: 'Cleared', detail: this.translateServ.dictionary['QUEUE_CLEAR_DOC'], life: 4000, sticky: false });
	}

	getStatusSeverity(status: string): string {
		switch (status) {
			case 'Pending':
				return 'info';
			case 'Uploading':
				return 'warning';
			case 'Uploaded':
				return 'success';
			case 'Failed':
				return 'danger';
			default:
				return 'secondary';
		}
	}

	formatBytes(bytes: number): string {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
	}

	private isValidFileType(file: File): boolean {
		if (!file.type) {
			return false;
		}
		return this.allowedFileTypesRegex.test(file.type);
	}

	private buildAcceptRegex(accept: string): RegExp {
		const types = accept.split(',').map(type => {
			type = type.trim();
			if (type.endsWith('/*')) {
				return type.replace(/\/\*/, '/.*');
			}
			return type.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		});
		return new RegExp(`^(${types.join('|')})$`, 'i');
	}


	fetchJobStatusData(): void {
		this.jobStatusLoading = true;
		this.ajaxService.getData(this.jobStatusApiUrl)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (response) => {
					const data = response as JobData[];
					this.jobStatusData = data;
					this.jobStatusLoading = false;
				},
				error: (error) => {
					console.error('Error fetching job status data:', error);
					this.jobStatusLoading = false;
					this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load job statuses', life: 7000, sticky: false});
				}
			});
	}

	setupJobStatusAutoRefresh(): void {
		// Unsubscribe from existing interval to prevent multiple subscriptions
		if (this.jobStatusRefreshSubscription) {
			this.jobStatusRefreshSubscription.unsubscribe();
		}
		this.jobStatusRefreshSubscription = interval(30000).subscribe(() => {
			this.fetchJobStatusData();
			console.log('Auto-refreshing job status data...');
		});
	}

	manualJobStatusRefresh(): void {
		this.fetchJobStatusData();
		console.log('Manual job status refresh triggered.');
	}

	getJobStatusProgressBarValue(status: string): number {
		const statusIndex = this.orderedStatuses.indexOf(status);
		
		if (statusIndex === -1 || status === 'Unknown Status') {
			return 0; // For unknown or unlisted statuses, show 0%
		}
		if (status === 'Completed') {
			return 100;
		}

		// Find the index of 'Completed' to use as the 100% mark
		const completedIndex = this.orderedStatuses.indexOf('Completed');
		if (completedIndex === -1 || completedIndex === 0) { // Avoid division by zero or incorrect calculation if 'Completed' is missing or first
			return 0; // Fallback if 'Completed' isn't found or is the very first status
		}

		// Calculate percentage based on current index relative to 'Completed' index
		return Math.min(100, Math.round((statusIndex / completedIndex) * 100));
	}

	getJobStatusSeverity(status: string): string {
		switch (status) {
			case 'Completed':
				return 'success';
			case 'GCP processing':
			case 'Postprocessing':
				return 'info';
			case 'Unknown Status':
				return 'danger';
			default:
				return 'secondary';
		}
	}
}